import React, { useState, useEffect, useRef, useCallback } from "react";
import { gsap } from "gsap";
import { Theme } from "types";

interface ColorOption {
  name: string;
  accentColor: string;
  accentBright: string;
  preview: string;
  icon: string;
}

interface ThemeColorPickerProps {
  currentTheme: Theme;
  onColorChange: (accentColor: string, accentBright: string) => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  showFloatingButton?: boolean;
}

// Add custom animations
const styles = `
  .animate-fade-in {
    animation: fadeIn 0.3s ease-out forwards;
  }
`;

// Inject styles
if (typeof document !== "undefined") {
  const styleSheet = document.createElement("style");
  styleSheet.type = "text/css";
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

const colorOptions: ColorOption[] = [
  {
    name: "Indigo",
    accentColor: "#6366F1",
    accentBright: "#8B5CF6",
    preview: "from-indigo-500 to-purple-600",
    icon: "🎨",
  },
  {
    name: "Rose",
    accentColor: "#E3405F",
    accentBright: "#FC1056",
    preview: "from-rose-500 to-pink-600",
    icon: "🌹",
  },
  {
    name: "Emerald",
    accentColor: "#10B981",
    accentBright: "#059669",
    preview: "from-emerald-500 to-green-600",
    icon: "💚",
  },
  {
    name: "Amber",
    accentColor: "#F59E0B",
    accentBright: "#D97706",
    preview: "from-amber-500 to-orange-600",
    icon: "⚡",
  },
  {
    name: "Cyan",
    accentColor: "#06B6D4",
    accentBright: "#0891B2",
    preview: "from-cyan-500 to-teal-600",
    icon: "🌊",
  },
  {
    name: "Violet",
    accentColor: "#8B5CF6",
    accentBright: "#A855F7",
    preview: "from-violet-500 to-purple-600",
    icon: "💜",
  },
  {
    name: "Random",
    accentColor: "#6366F1", // Default, will be randomized
    accentBright: "#8B5CF6",
    preview: "from-gray-400 to-gray-600",
    icon: "🎲",
  },
];

const ThemeColorPicker: React.FC<ThemeColorPickerProps> = ({
  currentTheme,
  onColorChange,
  isOpen: externalIsOpen,
  onOpenChange,
  showFloatingButton = true,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen ?? internalIsOpen;
  const setIsOpen = (value: boolean) => {
    setInternalIsOpen(value);
    onOpenChange?.(value);
  };
  const [selectedColor, setSelectedColor] = useState(colorOptions[0]);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Find the current color from options
    const current = colorOptions.find(
      (option) => option.accentColor === currentTheme.accentColor,
    );
    if (current) {
      setSelectedColor(current);
    }
  }, [currentTheme.accentColor]);

  // GSAP animations
  useEffect(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (isOpen && panelRef.current && !prefersReducedMotion) {
      const tl = gsap.timeline();

      // Panel entrance
      tl.fromTo(
        panelRef.current,
        {
          opacity: 0,
          y: 30,
          scale: 0.9,
          transformOrigin: "bottom right",
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.4,
          ease: "back.out(1.7)",
        },
      );

      // Stagger color buttons
      const buttons = panelRef.current.querySelectorAll("button");
      tl.fromTo(
        buttons,
        {
          opacity: 0,
          y: 20,
          scale: 0.8,
        },
        {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.3,
          stagger: 0.05,
          ease: "back.out(1.5)",
        },
        "-=0.2",
      );
    }
  }, [isOpen]);

  // Exit animation
  const handleClose = useCallback(() => {
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (panelRef.current && !prefersReducedMotion) {
      gsap.to(panelRef.current, {
        opacity: 0,
        y: 20,
        scale: 0.95,
        duration: 0.3,
        ease: "power2.in",
        onComplete: () => setIsOpen(false),
      });
    } else {
      setIsOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen, handleClose]);

  const generateRandomColor = () => {
    const colors = [
      "#FF6B6B",
      "#4ECDC4",
      "#45B7D1",
      "#96CEB4",
      "#FFEAA7",
      "#DDA0DD",
      "#98D8C8",
      "#F7DC6F",
      "#BB8FCE",
      "#85C1E9",
    ];
    const brightColors = [
      "#FF5252",
      "#26A69A",
      "#1976D2",
      "#4CAF50",
      "#FFC107",
      "#9C27B0",
      "#009688",
      "#FF9800",
      "#E91E63",
      "#2196F3",
    ];
    const randomIndex = Math.floor(Math.random() * colors.length);
    return {
      accentColor: colors[randomIndex],
      accentBright: brightColors[randomIndex],
    };
  };

  const handleColorSelect = (color: ColorOption) => {
    let finalColor = color;

    if (color.name === "Random") {
      const randomColors = generateRandomColor();
      finalColor = {
        ...color,
        accentColor: randomColors.accentColor,
        accentBright: randomColors.accentBright,
      };
    }

    setSelectedColor(finalColor);
    onColorChange(finalColor.accentColor, finalColor.accentBright);
    setIsOpen(false);

    // Save to localStorage
    localStorage.setItem(
      "portfolio-theme-color",
      JSON.stringify({
        accentColor: finalColor.accentColor,
        accentBright: finalColor.accentBright,
      }),
    );
  };

  return (
    <div className="relative">
      {/* Color Picker Button */}
      {showFloatingButton && (
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="fixed z-50 w-16 h-16 transition duration-300 transform border-2 border-white rounded-full shadow-lg bottom-6 right-6 hover:shadow-xl motion-safe:hover:scale-110 dark:border-gray-800 group"
          style={{
            background: `linear-gradient(135deg, ${selectedColor.accentColor}, ${selectedColor.accentBright})`,
          }}
          aria-label="Change theme color"
        >
          <div className="relative flex items-center justify-center w-full h-full rounded-full">
            <span className="text-xl transition-transform duration-200 group-hover:scale-110">
              {selectedColor.icon}
            </span>
            <div className="absolute inset-0 transition-all duration-200 bg-black bg-opacity-0 rounded-full group-hover:bg-opacity-10"></div>
          </div>
        </button>
      )}

      {/* Color Options Panel */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 transition-opacity duration-300 bg-black bg-opacity-20 backdrop-blur-sm"
            onClick={handleClose}
            aria-hidden="true"
          />

          {/* Color Picker Panel */}
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Theme color picker"
            className="fixed bottom-24 right-6 z-50 bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-3 min-w-[240px] transform"
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="flex items-center gap-1 text-base font-bold text-gray-900 dark:text-white">
                <span className="text-lg">🎨</span>
                Theme Color
              </h3>
              <button
                type="button"
                onClick={handleClose}
                className="p-1 transition duration-200 transform rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 motion-safe:hover:scale-110"
                aria-label="Close color picker"
              >
                <svg
                  className="w-5 h-5 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-2">
              {colorOptions.map((color) => (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => handleColorSelect(color)}
                  className={`relative p-2 rounded-lg border-2 transition duration-200 transform motion-safe:hover:scale-105 hover:shadow-lg group ${
                    selectedColor.name === color.name && color.name !== "Random"
                      ? "border-gray-900 dark:border-white shadow-lg ring-2 ring-offset-2 ring-gray-900 dark:ring-white"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                  }`}
                  aria-label={`Select ${color.name} theme`}
                >
                  {/* Color Preview */}
                  <div
                    className={`w-full h-10 rounded-md bg-gradient-to-br ${color.preview} mb-1 relative overflow-hidden`}
                  >
                    {color.name === "Random" && (
                      <div className="absolute inset-0 opacity-75 bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 via-purple-400 to-pink-400 motion-safe:animate-pulse"></div>
                    )}
                    <div className="absolute inset-0 transition duration-200 bg-black bg-opacity-0 rounded-lg group-hover:bg-opacity-10"></div>
                  </div>

                  <div className="flex items-center justify-center gap-1">
                    <span className="text-base">{color.icon}</span>
                    <div className="text-xs font-semibold text-center text-gray-700 dark:text-gray-300">
                      {color.name}
                    </div>
                  </div>

                  {/* Selected Indicator */}
                  {selectedColor.name === color.name &&
                    color.name !== "Random" && (
                      <div className="absolute flex items-center justify-center w-6 h-6 bg-gray-900 rounded-full shadow-lg -top-2 -right-2 dark:bg-white">
                        <svg
                          className="w-3.5 h-3.5 text-white dark:text-gray-900"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                </button>
              ))}
            </div>

            <div className="pt-2 mt-3 border-t border-gray-200 dark:border-gray-600">
              <p className="flex items-center justify-center gap-1 text-xs text-center text-gray-500 dark:text-gray-400">
                <span>💾</span>
                Auto-saved
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ThemeColorPicker;
